import { useCallback, useEffect, useRef, useState } from 'react';
import {
    DEFAULT_FLOATING_SIZE,
    FLOATING_ANCHOR_OFFSET,
    FLOATING_MIN_SIZE,
    STORAGE_KEYS,
} from '../config/modes';

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const readStoredSize = () => {
    if (typeof window === 'undefined') return null;
    try {
        const raw = window.localStorage.getItem(STORAGE_KEYS.FLOATING_SIZE);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') return null;
        return parsed;
    } catch {
        return null;
    }
};

const writeStoredSize = (size) => {
    if (typeof window === 'undefined') return;
    try {
        window.localStorage.setItem(STORAGE_KEYS.FLOATING_SIZE, JSON.stringify(size));
    } catch {
        // ignore
    }
};

const clampSizeToViewport = (raw, minSize, anchorOffset) => {
    if (typeof window === 'undefined') return raw;
    const maxWidth = Math.max(minSize.width, window.innerWidth - anchorOffset * 2);
    const maxHeight = Math.max(minSize.height, window.innerHeight - anchorOffset * 2);
    return {
        width: clamp(raw.width, minSize.width, maxWidth),
        height: clamp(raw.height, minSize.height, maxHeight),
    };
};

export function useFloatingWindow({
    defaultSize = DEFAULT_FLOATING_SIZE,
    minSize = FLOATING_MIN_SIZE,
    anchorOffset = FLOATING_ANCHOR_OFFSET,
} = {}) {
    const [size, setSize] = useState(() => {
        const stored = readStoredSize();
        const base = stored ? { ...defaultSize, ...stored } : { ...defaultSize };
        return clampSizeToViewport(base, minSize, anchorOffset);
    });

    const sizeRef = useRef(size);
    sizeRef.current = size;

    useEffect(() => {
        writeStoredSize(size);
    }, [size]);

    useEffect(() => {
        if (typeof window === 'undefined') return undefined;
        const onResize = () => {
            setSize((prev) => clampSizeToViewport(prev, minSize, anchorOffset));
        };
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [minSize.width, minSize.height, anchorOffset]);

    const startResize = useCallback(
        (event) => {
            if (event.button !== 0) return;
            event.preventDefault();
            event.stopPropagation();
            const startX = event.clientX;
            const startY = event.clientY;
            const origin = { ...sizeRef.current };

            const onMove = (ev) => {
                const deltaX = startX - ev.clientX;
                const deltaY = startY - ev.clientY;
                const maxWidth = window.innerWidth - anchorOffset * 2;
                const maxHeight = window.innerHeight - anchorOffset * 2;
                setSize({
                    width: clamp(origin.width + deltaX, minSize.width, maxWidth),
                    height: clamp(origin.height + deltaY, minSize.height, maxHeight),
                });
            };

            const onUp = () => {
                window.removeEventListener('mousemove', onMove);
                window.removeEventListener('mouseup', onUp);
                document.body.style.userSelect = '';
            };

            document.body.style.userSelect = 'none';
            window.addEventListener('mousemove', onMove);
            window.addEventListener('mouseup', onUp);
        },
        [minSize.width, minSize.height, anchorOffset],
    );

    return { size, setSize, startResize };
}
